import { Ban, Check, KeyRound, Plus, Trash2, Users as UsersIcon } from 'lucide-react';
import { useState } from 'react';
import { ConfirmDialog, Modal, useToast } from '../../components/ui/overlays';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Input,
  Select,
  Table,
  Td,
  Th,
  Tr,
} from '../../components/ui/primitives';
import {
  useCreateUser,
  useDeleteUser,
  useSetUserPassword,
  useUpdateUser,
  useUsers,
} from '../../hooks/queries';
import { formatRelative } from '../../lib/utils';
import type { User } from '../../types/api';

const PASSWORD_MIN = 12;

/** Admin-only account management. Rendered by SettingsPage when the session has the admin scope. */
export const UsersCard = ({ currentUserId }: { currentUserId: string }) => {
  const { data: users } = useUsers();
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const setPassword = useSetUserPassword();
  const deleteUser = useDeleteUser();
  const toast = useToast();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<User | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);

  const act = async <T,>(promise: Promise<T>, message: string): Promise<void> => {
    try {
      await promise;
      toast.success(message);
    } catch (error) {
      toast.error(error);
    }
  };

  return (
    <Card className="mt-4">
      <CardHeader
        title="Users"
        description="Everyone who can sign in. Members can do the go-to-market work; admins can also manage accounts and MCP clients."
        actions={
          <Button size="sm" icon={<Plus className="h-4 w-4" />} onClick={() => setInviteOpen(true)}>
            Add user
          </Button>
        }
      />

      {users?.length ? (
        <Table>
          <thead>
            <tr>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Role</Th>
              <Th>Status</Th>
              <Th>Last sign-in</Th>
              <Th className="w-36" />
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              const isSelf = user.id === currentUserId;
              return (
                <Tr key={user.id}>
                  <Td>
                    <span className="font-medium text-ink">{user.name}</span>
                    {isSelf ? (
                      <Badge tone="brand" className="ml-2">
                        you
                      </Badge>
                    ) : null}
                    {user.mustChangePassword ? (
                      <Badge tone="warning" className="ml-2">
                        must change password
                      </Badge>
                    ) : null}
                  </Td>
                  <Td className="text-muted">{user.email}</Td>
                  <Td>
                    <Select
                      className="h-8 w-auto text-[13px]"
                      value={user.role}
                      disabled={isSelf}
                      title={isSelf ? 'You cannot change your own role' : undefined}
                      onChange={(event) =>
                        act(
                          updateUser.mutateAsync({ id: user.id, role: event.target.value as User['role'] }),
                          `${user.name} is now ${event.target.value === 'ADMIN' ? 'an admin' : 'a member'}`,
                        )
                      }
                    >
                      <option value="ADMIN">Admin</option>
                      <option value="MEMBER">Member</option>
                    </Select>
                  </Td>
                  <Td>
                    {user.isActive ? (
                      <Badge tone="positive">active</Badge>
                    ) : (
                      <Badge tone="muted">deactivated</Badge>
                    )}
                  </Td>
                  <Td className="whitespace-nowrap text-faint">
                    {user.lastLoginAt ? formatRelative(user.lastLoginAt) : 'never'}
                  </Td>
                  <Td>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Reset password"
                        title="Set a new password"
                        onClick={() => setResetTarget(user)}
                      >
                        <KeyRound className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={isSelf}
                        aria-label={user.isActive ? 'Deactivate' : 'Reactivate'}
                        title={
                          isSelf
                            ? 'You cannot deactivate yourself'
                            : user.isActive
                              ? 'Deactivate — ends their sessions immediately'
                              : 'Reactivate'
                        }
                        onClick={() =>
                          act(
                            updateUser.mutateAsync({ id: user.id, isActive: !user.isActive }),
                            user.isActive
                              ? `${user.name} deactivated and signed out`
                              : `${user.name} reactivated`,
                          )
                        }
                      >
                        {user.isActive ? <Ban className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={isSelf}
                        aria-label="Delete user"
                        onClick={() => setDeleteTarget(user)}
                      >
                        <Trash2 className="h-4 w-4 text-danger" />
                      </Button>
                    </div>
                  </Td>
                </Tr>
              );
            })}
          </tbody>
        </Table>
      ) : (
        <EmptyState icon={<UsersIcon className="h-5 w-5" />} title="No users" />
      )}

      <InviteModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        busy={createUser.isPending}
        onSubmit={async (values) => {
          try {
            await createUser.mutateAsync(values);
            toast.success(`${values.email} can now sign in`);
            setInviteOpen(false);
          } catch (error) {
            toast.error(error);
          }
        }}
      />

      <ResetPasswordModal
        user={resetTarget}
        onClose={() => setResetTarget(null)}
        busy={setPassword.isPending}
        onSubmit={async (password) => {
          if (!resetTarget) return;
          try {
            await setPassword.mutateAsync({ id: resetTarget.id, password });
            toast.success(`New password set for ${resetTarget.name}. They are signed out everywhere.`);
            setResetTarget(null);
          } catch (error) {
            toast.error(error);
          }
        }}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title="Delete user"
        destructive
        confirmLabel="Delete"
        loading={deleteUser.isPending}
        message={
          <>
            Delete <strong className="text-ink">{deleteTarget?.email}</strong>? They lose access
            immediately. Leads and emails they worked on are kept.
          </>
        }
        onConfirm={async () => {
          if (!deleteTarget) return;
          try {
            await deleteUser.mutateAsync(deleteTarget.id);
            toast.success('User deleted');
            setDeleteTarget(null);
          } catch (error) {
            toast.error(error);
          }
        }}
      />
    </Card>
  );
};

const InviteModal = ({
  open,
  onClose,
  busy,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  busy: boolean;
  onSubmit: (values: { name: string; email: string; password: string; role: string }) => void;
}) => {
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'MEMBER' });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a user"
      description="You set the first password and share it with them; they are asked to change it after signing in."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!form.name.trim() || !form.email.trim() || form.password.length < PASSWORD_MIN}
            onClick={() => onSubmit({ ...form, email: form.email.trim().toLowerCase() })}
          >
            Add user
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name" htmlFor="invite-name">
          <Input
            id="invite-name"
            autoFocus
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
          />
        </Field>
        <Field label="Email" htmlFor="invite-email">
          <Input
            id="invite-email"
            type="email"
            value={form.email}
            onChange={(event) => setForm({ ...form, email: event.target.value })}
          />
        </Field>
        <Field
          label="Temporary password"
          htmlFor="invite-password"
          hint={`At least ${PASSWORD_MIN} characters. They will be prompted to replace it.`}
        >
          <Input
            id="invite-password"
            value={form.password}
            onChange={(event) => setForm({ ...form, password: event.target.value })}
          />
        </Field>
        <Field
          label="Role"
          htmlFor="invite-role"
          hint="Members work on leads and outreach. Admins can also manage users and MCP clients."
        >
          <Select
            id="invite-role"
            value={form.role}
            onChange={(event) => setForm({ ...form, role: event.target.value })}
          >
            <option value="MEMBER">Member</option>
            <option value="ADMIN">Admin</option>
          </Select>
        </Field>
      </div>
    </Modal>
  );
};

const ResetPasswordModal = ({
  user,
  onClose,
  busy,
  onSubmit,
}: {
  user: User | null;
  onClose: () => void;
  busy: boolean;
  onSubmit: (password: string) => void;
}) => {
  const [password, setPassword] = useState('');

  return (
    <Modal
      open={Boolean(user)}
      onClose={() => {
        setPassword('');
        onClose();
      }}
      title="Set a new password"
      description={user ? `For ${user.email}. This signs them out everywhere.` : ''}
      width="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={password.length < PASSWORD_MIN}
            onClick={() => {
              onSubmit(password);
              setPassword('');
            }}
          >
            Set password
          </Button>
        </>
      }
    >
      <Field label="New password" htmlFor="reset-password" hint={`At least ${PASSWORD_MIN} characters.`}>
        <Input
          id="reset-password"
          autoFocus
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </Field>
    </Modal>
  );
};
